import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";
import { useT } from "@/lib/i18n";

const FAQ_EN = [
  {
    q: "How fast is your turnaround?",
    a: "Most orders are cleaned and back at your door in 48 hours or less. Wedding gowns and leather pieces take longer — usually 7–14 days — and we'll always let you know up front.",
  },
  {
    q: "Is pickup and delivery really free?",
    a: "Yes — completely free across Chandler, Maricopa, Ahwatukee, Tempe, and Gilbert. No minimums, no service fees. Just schedule online and we'll be at your door.",
  },
  {
    q: "How do I pay?",
    a: "We charge after we weigh and inspect your order — so you only pay for what we clean. Cash, all major credit cards, and tap-to-pay accepted at pickup, drop-off, or in the store.",
  },
  {
    q: "What if you can't get a stain out?",
    a: "We tell you before we charge. If a stain is set or risky to remove, we'll attempt it at our own cost and let you know the result. No surprises.",
  },
  {
    q: "Do you handle delicate items — silk, wool, cashmere?",
    a: "Absolutely. Our dry-cleaning process is gentle on delicate fabrics. We hand-press silks and finish wool/cashmere with care so they keep their shape.",
  },
  {
    q: "Can you do alterations?",
    a: "Yes — in-house. Hems, waist adjustments, sleeve length, zipper replacements, and more. Drop off in store or ask our driver to bring it back for tailoring.",
  },
  {
    q: "Do you preserve wedding gowns?",
    a: "We do heirloom cleaning, preservation, and acid-free boxing for wedding gowns. Ask for a quote — pricing depends on fabric, beading, and storage size.",
  },
  {
    q: "Where exactly is your store?",
    a: "5865 W Ray Rd #3, Chandler, AZ 85226 — corner of Ray Rd & 56th St. Open Mon–Fri 7am–5pm and Saturday 10am–2pm.",
  },
  {
    q: "Do you offer discounts for veterans or first responders?",
    a: "Yes — 10% off every visit, no expiration. Just mention your service when you book or come in. Thank you for your service.",
  },
  {
    q: "How long has Oxford Cleaners been in Chandler?",
    a: "Since 1993 — over 30 years at the same Ray Rd & 56th St location. The shop is now under proud new ownership, and we've kept everything our long-time neighbors love while adding free pickup & delivery and a friendlier booking experience.",
  },
  {
    q: "Can I cancel or reschedule a pickup?",
    a: "Of course — just give us a call at 480-940-6023 by 8pm the night before. No fees, no fuss.",
  },
];

const FAQ_ES = [
  {
    q: "¿Qué tan rápido es el tiempo de entrega?",
    a: "La mayoría de los pedidos están limpios y de vuelta en tu puerta en 48 horas o menos. Los vestidos de novia y las piezas de cuero tardan más — usualmente 7 a 14 días — y siempre te avisaremos por adelantado.",
  },
  {
    q: "¿La recogida y entrega son realmente gratis?",
    a: "Sí — completamente gratis en Chandler, Maricopa, Ahwatukee, Tempe y Gilbert. Sin mínimos, sin cargos. Solo reserva en línea y estaremos en tu puerta.",
  },
  {
    q: "¿Cómo pago?",
    a: "Cobramos después de pesar e inspeccionar tu pedido — así solo pagas por lo que limpiamos. Aceptamos efectivo, tarjetas de crédito y pagos sin contacto.",
  },
  {
    q: "¿Y si no pueden quitar una mancha?",
    a: "Te avisamos antes de cobrar. Si una mancha está fijada o es riesgosa, intentaremos quitarla sin costo adicional y te avisaremos del resultado. Sin sorpresas.",
  },
  {
    q: "¿Manejan prendas delicadas — seda, lana, cachemira?",
    a: "Por supuesto. Nuestro proceso de tintorería es suave con telas delicadas. Planchamos seda a mano y terminamos lana/cachemira con cuidado para mantener su forma.",
  },
  {
    q: "¿Hacen arreglos de ropa?",
    a: "Sí — en el local. Dobladillos, ajustes de cintura, largo de mangas, reemplazo de cierres y más. Tráelo a la tienda o pide al conductor que lo lleve para arreglo.",
  },
  {
    q: "¿Preservan vestidos de novia?",
    a: "Hacemos limpieza de herencia, preservación y empaque sin ácido para vestidos de novia. Pide una cotización — el precio depende de la tela, pedrería y tamaño.",
  },
  {
    q: "¿Dónde está exactamente su tienda?",
    a: "5865 W Ray Rd #3, Chandler, AZ 85226 — esquina de Ray Rd y 56th St. Abierto Lun–Vie 7am–5pm y Sábado 10am–2pm.",
  },
  {
    q: "¿Ofrecen descuentos para veteranos o socorristas?",
    a: "Sí — 10% de descuento en cada visita, sin expiración. Solo menciona tu servicio al reservar o al entrar. Gracias por tu servicio.",
  },
  {
    q: "¿Cuánto tiempo lleva Oxford Cleaners en Chandler?",
    a: "Desde 1993 — más de 30 años en la misma ubicación de Ray Rd y 56th St. La tienda ahora está bajo nueva administración orgullosa, manteniendo todo lo que aman nuestros vecinos de toda la vida y agregando recogida y entrega gratis con una experiencia de reserva más amigable.",
  },
  {
    q: "¿Puedo cancelar o reagendar una recogida?",
    a: "Por supuesto — solo llámanos al 480-940-6023 antes de las 8pm la noche anterior. Sin cargos, sin problemas.",
  },
];

export default function FAQ() {
  const { t, lang } = useT();
  const items = lang === "es" ? FAQ_ES : FAQ_EN;

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 lg:py-28 bg-white"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-lightblue text-brand-blueDark px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]">
            <HelpCircle className="h-3 w-3" /> {t("faq.tag")}
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-navy">
            {t("faq.title")}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            {t("faq.subtitle")}
          </p>
        </div>

        <Accordion
          type="single"
          collapsible
          className="mt-12 rounded-3xl border border-slate-200 bg-white divide-y divide-slate-200 overflow-hidden"
          data-testid="faq-accordion"
        >
          {items.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="px-6 sm:px-8 border-0"
            >
              <AccordionTrigger className="py-5 sm:py-6 text-left hover:no-underline group">
                <span className="font-display text-base sm:text-lg font-semibold text-brand-navy group-hover:text-brand-blue transition-colors pr-4">
                  {item.q}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-5 sm:pb-6 text-slate-600 leading-relaxed text-sm sm:text-base">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
